import isEqual from 'lodash/isEqual';
import { Dispatch } from 'redux';

import { Events } from 'src/constants/webSocketConsts/events';
import { AppBuilder, appBuilderApi } from 'src/services/api/appBuilderApi';

// Fields whose change alone is not worth a cache write. The backend re-stamps
// updatedAt on bookkeeping writes every few seconds during a session; each
// write used to re-render the whole builder page (and rebuild every chat row)
// for a field nothing in the builder route displays.
const BOOKKEEPING_FIELDS = new Set<keyof AppBuilder>(['updatedAt']);

// Lists the backend drops from the record when empty. Every websocket message
// carries the whole record, so one of these missing means it was emptied;
// merging only the fields present would keep the old list on the page.
const CLEARED_WHEN_ABSENT: readonly (keyof AppBuilder)[] = [
  'requiredSecrets',
  'requiredIntegrations',
  'publishedRequiredIntegrations',
  'draftEvents',
];

export function handleAppBuilderUpdate(
  event: Events,
  items: AppBuilder[],
  dispatch: Dispatch<any>,
) {
  const builder = items.at(0);
  if (!builder) {
    return;
  }
  if (event === Events.Remove) {
    return;
  }
  if (event === Events.Modify || event === Events.Insert) {
    dispatch(
      appBuilderApi.util.updateQueryData(
        'getAppBuilder',
        { id: builder.id },
        (draft: AppBuilder) => {
          const changed = (Object.keys(builder) as (keyof AppBuilder)[]).filter(
            (key) => !isEqual(draft[key], builder[key]),
          );
          // An already-empty list needs no write, so the frequent updatedAt-only
          // messages stay no-ops.
          const emptied = CLEARED_WHEN_ABSENT.filter(
            (key) =>
              !(key in builder) &&
              Array.isArray(draft[key]) &&
              (draft[key] as unknown[]).length > 0,
          );
          // A recipe that writes nothing yields no patches, and RTK Query then
          // dispatches nothing: no store notification, no re-render.
          if (
            emptied.length === 0 &&
            changed.every((key) => BOOKKEEPING_FIELDS.has(key))
          ) {
            return;
          }
          for (const key of changed) {
            (draft as unknown as Record<string, unknown>)[key] = builder[key];
          }
          for (const key of emptied) {
            delete draft[key];
          }
        },
      ),
    );
  }
}
